/**
 * Metro 2 Charge-Off Review Panel
 * 
 * Specialized review for charge-off accounts:
 * - Charge-Off Balance Review Needed
 * - Charge-Off Balance / Ownership Conflict
 * - Paid Charge-Off Balance Conflict
 * - Settlement Balance Review Needed
 * - Discharge Balance Conflict
 * - Duplicate Balance / Ownership Conflict
 * - Charge-Off Payment Status Conflict
 * - Charge-Off Payment History Review
 * - Bureau Balance Inconsistency
 * - DOFD / Re-Aging Review
 */

import React from 'react';
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  DollarSign,
  Calendar,
  FileText,
  ArrowRight,
  Copy,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';

// Charge-off issue configurations
const CHARGEOFF_ISSUES = {
  chargeoff_balance_review_needed: {
    label: 'Charge-Off Balance Review Needed',
    icon: DollarSign,
    severity: 'medium',
    description: 'Charged-off account shows balance > $0. May be accurate if debt is unpaid and not sold.',
  },
  chargeoff_balance_ownership_conflict: {
    label: 'Charge-Off Balance / Ownership Conflict',
    icon: AlertTriangle,
    severity: 'high',
    description: 'Charge-off shows balance while account is marked sold, transferred, paid, settled, or discharged.',
  },
  paid_chargeoff_balance_conflict: {
    label: 'Paid Charge-Off Balance Conflict',
    icon: DollarSign,
    severity: 'high',
    description: 'Paid or settled charge-off still reports balance > $0.',
  },
  settlement_balance_review_needed: {
    label: 'Settlement Balance Review Needed',
    icon: FileText,
    severity: 'medium',
    description: 'Settled account balance reporting needs verification.',
  },
  discharge_balance_conflict: {
    label: 'Discharge Balance Conflict',
    icon: AlertTriangle,
    severity: 'high',
    description: 'Discharged (bankruptcy) account still reports balance > $0.',
  },
  duplicate_balance_ownership_conflict: {
    label: 'Duplicate Balance / Ownership Conflict',
    icon: Copy,
    severity: 'high',
    description: 'Original creditor and collection agency both report same balance.',
  },
  chargeoff_payment_status_conflict: {
    label: 'Charge-Off Payment Status Conflict',
    icon: AlertCircle,
    severity: 'high',
    description: 'Charge-off reports Late 120 payment status instead of charge-off/derogatory status.',
  },
  chargeoff_payment_history_review: {
    label: 'Charge-Off Payment History Review',
    icon: Calendar,
    severity: 'medium',
    description: 'Charge-off payment history grid needs review for proper CO reporting.',
  },
  bureau_balance_inconsistency: {
    label: 'Bureau Balance Inconsistency',
    icon: AlertTriangle,
    severity: 'high',
    description: 'Balance differs across bureaus for the same charge-off account.',
  },
  dofd_reaging_review: {
    label: 'DOFD / Re-Aging Review',
    icon: Calendar,
    severity: 'high',
    description: 'Date of First Delinquency may be inconsistent or indicate re-aging.',
  },
};

// Severity badge colors
const SEVERITY_COLORS = {
  high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-700',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-700',
  low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-700',
};

// Charge-Off Issue Item
const ChargeOffIssueItem = ({ issueKey, details }) => {
  const config = CHARGEOFF_ISSUES[issueKey];
  if (!config) return null;

  const IconComponent = config.icon;

  return (
    <div className={`p-3 rounded-lg border ${
      config.severity === 'high' 
        ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-700' 
        : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700'
    }`}>
      <div className="flex items-start gap-3">
        <IconComponent className={`w-5 h-5 mt-0.5 ${
          config.severity === 'high' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'
        }`} />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`font-medium ${
              config.severity === 'high' ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'
            }`}>
              {config.label}
            </span>
            <Badge variant="outline" className={SEVERITY_COLORS[config.severity]}>
              {config.severity}
            </Badge>
          </div>
          <p className={`text-sm ${
            config.severity === 'high' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'
          }`}>
            {details || config.description}
          </p>
        </div>
      </div>
    </div>
  );
};

// Charge-Off Account Details
const ChargeOffAccountDetails = ({ account, bureauData }) => {
  const balance = account?.balance || 0;
  const originalChargeOffAmount = account?.original_chargeoff_amount || account?.chargeoff_amount;
  const creditLimit = account?.credit_limit || account?.high_credit;
  const pastDue = account?.past_due || 0;
  const chargeOffDate = account?.chargeoff_date || account?.date_closed;
  const dofd = account?.dofd || account?.date_of_first_delinquency;
  const lastPayment = account?.date_last_payment || account?.dateLastPayment;
  const accountStatus = account?.account_status || account?.status;
  const paymentStatus = account?.payment_status;

  // Check for sold/transferred indicators
  const remarks = account?.remarks || account?.comments || '';
  const isSold = remarks.toLowerCase().includes('sold') || remarks.toLowerCase().includes('transferred');
  const isPaid = remarks.toLowerCase().includes('paid') || remarks.toLowerCase().includes('settled');
  const isDischarged = remarks.toLowerCase().includes('discharged') || remarks.toLowerCase().includes('bankruptcy');

  return (
    <div className="space-y-4">
      {/* Main Details Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-stone-50 dark:bg-stone-800/50 rounded-lg">
        <div>
          <span className="text-xs text-stone-500 dark:text-stone-400 block">Current Balance</span>
          <span className={`text-sm font-medium ${
            balance > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'
          }`}>
            ${(balance || 0).toLocaleString()}
          </span>
        </div>
        <div>
          <span className="text-xs text-stone-500 dark:text-stone-400 block">Original Charge-Off</span>
          <span className="text-sm font-medium text-stone-800 dark:text-stone-200">
            {originalChargeOffAmount ? `$${originalChargeOffAmount.toLocaleString()}` : 'N/A'}
          </span>
        </div>
        <div>
          <span className="text-xs text-stone-500 dark:text-stone-400 block">Past Due</span>
          <span className={`text-sm font-medium ${
            pastDue > 0 ? 'text-red-600 dark:text-red-400' : 'text-stone-800 dark:text-stone-200'
          }`}>
            ${(pastDue || 0).toLocaleString()}
          </span>
        </div>
        <div>
          <span className="text-xs text-stone-500 dark:text-stone-400 block">Credit Limit / High Credit</span>
          <span className="text-sm font-medium text-stone-800 dark:text-stone-200">
            {creditLimit ? `$${creditLimit.toLocaleString()}` : 'N/A'}
          </span>
        </div>
        <div>
          <span className="text-xs text-stone-500 dark:text-stone-400 block">Account Status</span>
          <span className="text-sm font-medium text-stone-800 dark:text-stone-200">
            {accountStatus || 'N/A'}
          </span>
        </div>
        <div>
          <span className="text-xs text-stone-500 dark:text-stone-400 block">Payment Status</span>
          <span className="text-sm font-medium text-stone-800 dark:text-stone-200">
            {paymentStatus || 'N/A'}
          </span>
        </div>
        <div>
          <span className="text-xs text-stone-500 dark:text-stone-400 block">Charge-Off Date</span>
          <span className="text-sm font-medium text-stone-800 dark:text-stone-200">
            {chargeOffDate || 'N/A'}
          </span>
        </div>
        <div>
          <span className="text-xs text-stone-500 dark:text-stone-400 block">DOFD</span>
          <span className={`text-sm font-medium ${
            !dofd ? 'text-amber-600 dark:text-amber-400' : 'text-stone-800 dark:text-stone-200'
          }`}>
            {dofd || 'Missing'}
          </span>
        </div>
      </div>

      {/* Status Indicators */}
      <div className="flex flex-wrap gap-2">
        {isSold && (
          <Badge variant="outline" className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
            Sold / Transferred
          </Badge>
        )}
        {isPaid && (
          <Badge variant="outline" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
            Paid / Settled
          </Badge>
        )}
        {isDischarged && (
          <Badge variant="outline" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
            Discharged
          </Badge>
        )}
        {balance > 0 && (isSold || isPaid || isDischarged) && (
          <Badge variant="outline" className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
            Balance Conflict
          </Badge>
        )}
      </div>

      {/* Bureau Comparison if available */}
      {bureauData && Object.keys(bureauData).length > 0 && (
        <div className="p-3 bg-stone-100 dark:bg-stone-800 rounded-lg">
          <h5 className="text-xs font-medium text-stone-600 dark:text-stone-400 mb-2">Balance by Bureau</h5>
          <div className="flex gap-4">
            {Object.entries(bureauData.balance || {}).map(([bureau, value]) => (
              <div key={bureau} className="text-sm">
                <span className="text-stone-500 dark:text-stone-400">{bureau}: </span>
                <span className="font-medium text-stone-800 dark:text-stone-200">
                  {typeof value === 'number' ? `$${value.toLocaleString()}` : value || 'N/A'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Metro 2 Charge-Off Review Panel
const Metro2ChargeOffReviewPanel = ({
  account,
  bureauData,
  detectedIssues = [],
  balanceIssues = [],
  ownershipIssues = [],
  paymentHistoryIssues = [],
  showAccountDetails = true,
}) => {
  // Combine all issues
  const allIssues = [
    ...detectedIssues,
    ...balanceIssues.map(i => typeof i === 'string' ? i : i.key),
    ...ownershipIssues.map(i => typeof i === 'string' ? i : i.key),
    ...paymentHistoryIssues.map(i => typeof i === 'string' ? i : i.key),
  ].filter((v, i, a) => a.indexOf(v) === i); // Dedupe

  const hasIssues = allIssues.length > 0;
  const highSeverityCount = allIssues.filter(i => CHARGEOFF_ISSUES[i]?.severity === 'high').length;

  return (
    <Card className="border-stone-200 dark:border-stone-700" data-testid="metro2-chargeoff-review-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2 text-stone-800 dark:text-stone-200">
            <DollarSign className="w-5 h-5 text-red-600 dark:text-red-400" />
            Charge-Off Account Review
          </CardTitle>
          <Badge 
            variant="outline" 
            className={hasIssues 
              ? highSeverityCount > 0
                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
              : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
            }
          >
            {hasIssues ? `${allIssues.length} Issue(s)` : 'Clean'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Charge-Off Notice */}
        <Alert className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700">
          <DollarSign className="h-4 w-4 text-red-600 dark:text-red-400" />
          <AlertTitle className="text-red-700 dark:text-red-300">Charge-Off Account Detected</AlertTitle>
          <AlertDescription className="text-red-600 dark:text-red-400">
            A charged-off account can report a balance if the original creditor still owns unpaid debt. 
            However, balance conflicts occur when the account is marked sold, transferred, paid, settled, 
            or discharged while still showing a balance.
          </AlertDescription>
        </Alert>

        {/* Account Details */}
        {showAccountDetails && account && (
          <ChargeOffAccountDetails account={account} bureauData={bureauData} />
        )}

        {/* Detected Issues */}
        {hasIssues ? (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300">
              Detected Charge-Off Issues
            </h4>
            {allIssues.map((issueKey, idx) => (
              <ChargeOffIssueItem key={idx} issueKey={issueKey} />
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
            <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <p className="text-sm text-emerald-700 dark:text-emerald-300">
              No charge-off balance or ownership conflicts detected. Account may still be disputable through standard Metro 2 review.
            </p>
          </div>
        )}

        {/* Acceptable CO Pattern Note */}
        <div className="p-3 bg-stone-100 dark:bg-stone-800 rounded-lg">
          <p className="text-xs text-stone-600 dark:text-stone-400">
            <strong>Metro 2 Note:</strong> Original creditor CO/CO/CO pattern may be acceptable if the debt is 
            unpaid, still owned by original creditor, DOFD not reset, and not sold/transferred/paid/settled/discharged. 
            Flag if CO repeats after sold/paid/settled/discharged.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default Metro2ChargeOffReviewPanel;
export { ChargeOffIssueItem, ChargeOffAccountDetails, CHARGEOFF_ISSUES };
